import { useState, useMemo, useCallback } from "react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { Switch } from "../../../../components/common/switch";
import { IconCopy, IconPercentage } from "@tabler/icons-react";
import { mutate } from "swr";
import type { ProductRequestDto, ReviewProductRequestDto, VariantPricingTiersDto, PricingTierResponseDto } from "../../../../api";
import { adminProductRequestsService } from "../../../api_client";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { ReviewProductRequestDtoStatusEnum } from "../../../../api";

interface ProductRequestApprovalFormProps {
  request: ProductRequestDto;
  onApprove?: (variantPricingTiers: VariantPricingTiersDto[]) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  readOnly?: boolean;
  title?: string;
  subtitle?: string;
}

function getTierLabel(minQty: number): { title: string; subtitle: string } {
  return { title: `${minQty}+ units`, subtitle: `Min qty: ${minQty}` };
}

function calcPrice(supplierCost: number, margin: number, roundUp: boolean): number {
  const raw = supplierCost * (1 + margin / 100);
  return roundUp ? Math.ceil(raw / 10) * 10 - 0.01 : Math.round(raw * 100) / 100;
}

/** Build a human-readable label for a variant from its attribute IDs. */
function variantLabel(v: ProductRequestDto["variants"][number], index: number): string {
  const parts: string[] = [];
  if (v.sku) parts.push(v.sku);
  if (v.model) parts.push(v.model);
  if (parts.length === 0) parts.push(`Variant ${index + 1}`);
  return parts.join(" — ");
}

export function ProductRequestApprovalForm({
  request,
  onApprove,
  onCancel,
  isSubmitting = false,
  readOnly = false,
  title = "Approve Product Request",
  subtitle = "Set the Procurtal pricing tiers per variant. These prices will be used when customers purchase this product.",
}: ProductRequestApprovalFormProps) {
  const variants = request.variants ?? [];

  // Per-variant margins: { [variantId]: { [minQty]: marginString } }
  const [marginsByVariant, setMarginsByVariant] = useState<Record<string, Record<number, string>>>(() => {
    const initial: Record<string, Record<number, string>> = {};
    for (const v of variants) {
      const tiers = [...(v.pricingTiers ?? [])].sort((a, b) => a.minQty - b.minQty);
      initial[v.id] = Object.fromEntries(tiers.map((t) => [t.minQty, ""]));
    }
    return initial;
  });

  const [roundUp, setRoundUp] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);

  /** Computed tiers with margin for each variant. */
  const variantTiersData = useMemo(() => {
    return variants.map((v) => {
      const supplierTiers = [...(v.pricingTiers ?? [])].sort((a, b) => a.minQty - b.minQty);
      const defaultTiers: PricingTierResponseDto[] = supplierTiers.length > 0
        ? supplierTiers
        : [{ minQty: 1, price: 0 }, { minQty: 5, price: 0 }, { minQty: 15, price: 0 }];
      const currency = supplierTiers[0]?.currency?.trim() || "QAR";

      const tiersWithMargin = defaultTiers.map((t) => {
        const savedTier = supplierTiers.find((tier) => tier.minQty === t.minQty) as typeof t & {
          adminPrice?: number | null;
          marginPercentage?: number | null;
        } | undefined;
        const m = readOnly
          ? Number(savedTier?.marginPercentage ?? NaN)
          : parseFloat(marginsByVariant[v.id]?.[t.minQty] ?? "");
        const hasMargin = readOnly
          ? savedTier?.adminPrice != null || savedTier?.marginPercentage != null
          : !isNaN(m) && m > 0;
        const procurtalPrice = readOnly
          ? (savedTier?.adminPrice ?? null)
          : hasMargin
            ? calcPrice(t.price, m, roundUp)
            : null;
        return { ...t, margin: m, hasMargin, procurtalPrice };
      });

      const configuredCount = tiersWithMargin.filter((t) => t.hasMargin).length;
      const allConfigured = configuredCount === defaultTiers.length;

      return { variant: v, supplierTiers, defaultTiers, currency, tiersWithMargin, configuredCount, allConfigured };
    });
  }, [variants, marginsByVariant, roundUp, readOnly]);

  const allVariantsConfigured = variantTiersData.every((vd) => vd.allConfigured);
  const totalConfigured = variantTiersData.reduce((sum, vd) => sum + vd.configuredCount, 0);
  const totalTiers = variantTiersData.reduce((sum, vd) => sum + vd.defaultTiers.length, 0);

  const updateMargin = useCallback((variantId: string, minQty: number, value: string) => {
    setMarginsByVariant((prev) => ({
      ...prev,
      [variantId]: { ...prev[variantId], [minQty]: value },
    }));
  }, []);

  /** Copy margins from the first variant to matching minQty tiers of every other variant. */
  const copyMarginsToAll = useCallback(() => {
    if (variants.length < 2) return;
    const firstId = variants[0].id;
    const firstMargins = marginsByVariant[firstId] ?? {};
    setMarginsByVariant((prev) => {
      const next = { ...prev };
      for (const v of variants.slice(1)) {
        const existing = prev[v.id] ?? {};
        const mapped: Record<number, string> = {};
        for (const key of Object.keys(existing)) {
          const minQty = Number(key);
          mapped[minQty] = firstMargins[minQty] ?? existing[minQty] ?? "";
        }
        next[v.id] = mapped;
      }
      return next;
    });
  }, [variants, marginsByVariant]);

  const [globalMargin, setGlobalMargin] = useState("");

  const applyGlobalMargin = useCallback(() => {
    if (!globalMargin) return;
    setMarginsByVariant((prev) => {
      const next: Record<string, Record<number, string>> = {};
      for (const v of variants) {
        const tierKeys = Object.keys(prev[v.id] ?? {}).map(Number);
        next[v.id] = Object.fromEntries(tierKeys.map((k) => [k, globalMargin]));
      }
      return next;
    });
  }, [variants, globalMargin]);

  const handleApprove = async () => {
    if (!allVariantsConfigured || !onApprove) return;
    setIsSubmittingLocal(true);
    try {
      const variantPricingTiers: VariantPricingTiersDto[] = variantTiersData.map((vd) => ({
        variantId: vd.variant.id,
        pricingTiers: vd.tiersWithMargin.map((t) => ({
          minQty: t.minQty,
          price: t.procurtalPrice!,
        })),
      }));
      await onApprove(variantPricingTiers);
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const handleReject = async () => {
    if (readOnly) return;
    if (!rejectReason.trim()) { showErrorToast("Please provide a rejection reason"); return; }
    setIsRejecting(true);
    try {
      await adminProductRequestsService.adminReviewProductRequest(request.id, {
        status: ReviewProductRequestDtoStatusEnum.Rejected,
        rejectionReason: rejectReason.trim(),
      } as ReviewProductRequestDto);
      showSuccessToast("Product request rejected successfully");
      mutate((key) => typeof key === "string" && (
        key.startsWith("admin-product-requests") ||
        key.startsWith("product-requests-grouped-by-user") ||
        key.startsWith("admin-product-request-details") ||
        key.startsWith("product-request-details")
      ));
      onCancel?.();
    } catch (err: unknown) {
      showErrorToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed to reject product request",
      );
    } finally {
      setIsRejecting(false);
    }
  };

  const submitting = isSubmitting || isSubmittingLocal;

  return (
    <div className="rounded-2xl border border-borderDefault bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 max-md:px-4">
        <Heading variant="h3" tone="dark">{title}</Heading>
        <Text variant="body" tone="body">{subtitle}</Text>
      </div>

      <div className="border-t border-borderLight px-6 py-4 flex flex-col gap-6 max-md:px-4">
        {/* Controls row */}
        {!readOnly && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 max-md:flex-wrap">
              <Input
                name="global-margin"
                type="number"
                min={0}
                value={globalMargin}
                onChange={(e) => setGlobalMargin(e.target.value)}
                placeholder="e.g. 15"
                rightAddon={<Text variant="bodySm" tone="muted">%</Text>}
                inputWrapperClassName="!h-auto !py-2 !rounded-lg w-28"
              />
              <Button
                variant="primary"
                size="sm"
                leftIcon={<IconPercentage size={16} />}
                onClick={applyGlobalMargin}
                disabled={!globalMargin}
              >
                Apply margin to all
              </Button>
            </div>
            <div className="flex items-center gap-3 max-md:flex-wrap">
              {variants.length > 1 && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<IconCopy size={16} />}
                  onClick={copyMarginsToAll}
                >
                  Copy margins from first variant
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Switch checked={roundUp} onChange={setRoundUp} aria-label="Round up prices to nearest 10" />
                <Text variant="bodySm" tone="dark">Round up to x9.99</Text>
              </div>
            </div>
          </div>
        )}

        {/* Per-variant sections */}
        {variantTiersData.map((vd, vIndex) => (
          <div key={vd.variant.id} className="flex flex-col gap-3">
            {/* Variant header + specs */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Heading variant="h3" tone="dark">
                  {variantLabel(vd.variant, vIndex)}
                </Heading>
                {!vd.allConfigured && !readOnly && (
                  <Text variant="bodyXs" tone="muted">
                    ({vd.configuredCount}/{vd.defaultTiers.length} tiers set)
                  </Text>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {vd.variant.sku && <Text variant="bodyXs" tone="muted">SKU: {vd.variant.sku}</Text>}
                {vd.variant.model && <Text variant="bodyXs" tone="muted">Model: {vd.variant.model}</Text>}
                {vd.variant.quantity != null && <Text variant="bodyXs" tone="muted">Qty: {vd.variant.quantity}</Text>}
              </div>
            </div>

            {/* Tier table */}
            <div className="rounded-xl border border-borderDefault overflow-hidden">
              {/* Header row */}
              {/* Phones: header and rows both fold into the same 2×2 grid. */}
              <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr] gap-4 bg-bgLight px-5 py-3 max-md:grid-cols-2 max-md:gap-x-3 max-md:gap-y-1 max-md:px-4">
                <Text variant="bodySm" tone="muted">Tier / Min Qty</Text>
                <Text variant="bodySm" tone="muted">Supplier Cost</Text>
                <Text variant="bodySm" tone="muted">Your Margin (%)</Text>
                <Text variant="bodySm" tone="muted" className="text-right">Procurtal Price</Text>
              </div>

              {/* Data rows */}
              {vd.tiersWithMargin.map((tier) => {
                const { title: tierTitle, subtitle: tierSubtitle } = getTierLabel(tier.minQty);
                return (
                  <div key={tier.minQty} className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr] gap-4 items-center px-5 py-4 border-t border-borderLight max-md:grid-cols-2 max-md:gap-3 max-md:px-4">
                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold" tone="dark">{tierTitle}</Text>
                      <Text variant="bodySm" tone="muted">{tierSubtitle}</Text>
                    </div>

                    <Text variant="bodyBold" tone="dark">{vd.currency} {tier.price.toLocaleString()}</Text>

                    <Input
                      name={`margin-${vd.variant.id}-${tier.minQty}`}
                      type="number"
                      min={0}
                      value={marginsByVariant[vd.variant.id]?.[tier.minQty] ?? ""}
                      onChange={(e) => updateMargin(vd.variant.id, tier.minQty, e.target.value)}
                      placeholder="e.g. 15"
                      rightAddon={<Text variant="bodySm" tone="muted">%</Text>}
                      inputWrapperClassName="!h-auto !py-2 !rounded-lg w-32"
                      disabled={readOnly}
                    />

                    <div className="flex justify-end">
                      {tier.hasMargin && tier.procurtalPrice != null ? (
                        <div className="inline-flex flex-col items-center rounded-xl bg-accentGreenTintBg px-4 py-2 w-fit">
                          <Text variant="bodyBold" tone="green">{vd.currency} {tier.procurtalPrice.toLocaleString()}</Text>
                          <Text variant="bodyXsBold" tone="green">
                            {readOnly
                              ? `+${Number.isFinite(tier.margin) ? tier.margin.toLocaleString(undefined, { maximumFractionDigits: 2 }) : tier.margin}% OVER COST`
                              : `+${tier.margin}% OVER COST`}
                          </Text>
                        </div>
                      ) : (
                        <Text variant="body" tone="muted">—</Text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Warning */}
        {!readOnly && !allVariantsConfigured && (
          <div className="flex items-center gap-2 rounded-xl bg-accentYellowTint px-4 py-3">
            <span className="text-accentAmber shrink-0">●</span>
            <Text variant="bodySm" tone="amber">
              {totalConfigured} of {totalTiers} tiers across {variants.length} variant{variants.length !== 1 ? "s" : ""} {totalConfigured === 1 ? "has" : "have"} pricing set. All tiers must be configured before you can approve.
            </Text>
          </div>
        )}

        {/* Reject reason form */}
        {!readOnly && showRejectForm && (
          <div className="flex flex-col gap-2">
            <Input
              name="rejectReason"
              label="Rejection reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection…"
            />
          </div>
        )}

        {/* Actions */}
        {!readOnly && (
          <div className="flex items-center gap-3 pt-1 max-md:flex-wrap">
            {!showRejectForm ? (
              <>
                <Button
                  variant="filledDark"
                  size="md"
                  onClick={handleApprove}
                  disabled={!allVariantsConfigured || submitting}
                >
                  {submitting ? "Approving…" : "Approve and set prices"}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setShowRejectForm(true)}
                  disabled={submitting}
                >
                  Reject product
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="filledDark"
                  size="md"
                  onClick={handleReject}
                  disabled={isRejecting || !rejectReason.trim()}
                >
                  {isRejecting ? "Rejecting…" : "Confirm reject"}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => { setShowRejectForm(false); setRejectReason(""); }}
                  disabled={isRejecting}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
